/** @format */

import {
	Box,
	Card,
	Chip,
	Divider,
	Grid,
	InputAdornment,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TablePagination,
	TableRow,
	Typography,
	IconButton,
	Tooltip,
	CircularProgress,
	Tab,
	Tabs,
	TextField,
	Alert,
	Paper,
	Collapse,
} from "@mui/material";
import OutlinedInput from "@mui/material/OutlinedInput";
import { MagnifyingGlassIcon } from "@phosphor-icons/react/dist/ssr/MagnifyingGlass";
import { ArrowsClockwise } from "@phosphor-icons/react/dist/ssr/ArrowsClockwise";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import PendingIcon from "@mui/icons-material/Pending";
import RefreshIcon from "@mui/icons-material/Refresh";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningIcon from "@mui/icons-material/Warning";
import ScheduleIcon from "@mui/icons-material/Schedule";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import React, { useEffect, useState, useCallback } from "react";
import dayjs, { Dayjs } from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch } from "../../../redux/store/store";
import { formatCurrency } from "../../../utility/currencyUtils";
import {
	getAutoDraftResultsByDateAsAdmin,
	getAutoDraftFailedAsAdmin,
	getAutoDraftRetriesAsAdmin,
	AutoDraftAttemptBase,
} from "../../../redux/actions/autoDraftAction";
import {
	selectAutoDraftResults,
	selectAutoDraftFailed,
	selectAutoDraftRetries,
	selectAutoDraftResultsLoading,
	selectAutoDraftResultsError,
} from "../../../redux/selectors/userSelector";
import { selectAdminCircles } from "../../../redux/selectors/circleSelector";
import { fetchAllCirclesAdmin } from "../../../redux/actions/circleAction";
import { LoadingSpinner } from "../../allComponents/AllComponents";
import UserProfileHeader from "../../userprofile-header/UserProfileHeader";
import DrawerNavbar from "../../drawerNavBar/DrawerNavbar";
import Main from "../../../utility/common/Main";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay, PickersDayProps } from "@mui/x-date-pickers/PickersDay";

// Tab types
type TabValue = "results" | "failed" | "retries";

// Custom day component for the calendar
const CustomPickersDay = (props: PickersDayProps<Dayjs>) => {
	const { day, selected, today, ...other } = props;

	return (
		<PickersDay
			{...other}
			day={day}
			selected={selected}
			today={today}
			sx={{
				...(selected && {
					backgroundColor: "primary.main",
					color: "primary.contrastText",
					"&:hover": {
						backgroundColor: "primary.dark",
					},
				}),
				...(today &&
					!selected && {
					border: "1px solid",
					borderColor: "primary.main",
				}),
			}}
		/>
	);
};

const AutoDraftReports = () => {
	const dispatch = useDispatch<AppDispatch>();

	// Selectors
	const autoDraftResults = useSelector(selectAutoDraftResults);
	const autoDraftFailed = useSelector(selectAutoDraftFailed);
	const autoDraftRetries = useSelector(selectAutoDraftRetries);
	const isLoading = useSelector(selectAutoDraftResultsLoading);
	const error = useSelector(selectAutoDraftResultsError);
	const adminCircles = useSelector(selectAdminCircles);

	// UI State
	const [openNav, setOpenNav] = useState(false);
	const [activeTab, setActiveTab] = useState<TabValue>("results");
	const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
	const [searchQuery, setSearchQuery] = useState("");
	const [showCalendar, setShowCalendar] = useState(false);

	// Pagination state for each tab
	const [resultsPage, setResultsPage] = useState(0);
	const [resultsRowsPerPage, setResultsRowsPerPage] = useState(10);
	const [failedPage, setFailedPage] = useState(0);
	const [failedRowsPerPage, setFailedRowsPerPage] = useState(10);
	const [retriesPage, setRetriesPage] = useState(0);
	const [retriesRowsPerPage, setRetriesRowsPerPage] = useState(10);

	// Expanded row state for failure analysis
	const [expandedFailureAnalysis, setExpandedFailureAnalysis] = useState(false);

	// Get user's timezone
	const userTimezone =
		Intl.DateTimeFormat().resolvedOptions().timeZone || "America/Chicago";

	// Helper function to get circle name by ID from adminCircles
	const getCircleName = useCallback(
		(circleId?: string, fallbackName?: string): string => {
			if (!circleId) return fallbackName || "N/A";
			const circle = adminCircles.find((c) => c._id === circleId);
			return circle?.circleName || fallbackName || "N/A";
		},
		[adminCircles],
	);

	// Fetch all circles on mount for lookup
	useEffect(() => {
		if (adminCircles.length === 0) {
			dispatch(fetchAllCirclesAdmin());
		}
	}, [dispatch, adminCircles.length]);

	// Fetch data on mount and when date changes
	const fetchResultsData = useCallback(() => {
		const dateStr = selectedDate.format("YYYY-MM-DD");
		dispatch(
			getAutoDraftResultsByDateAsAdmin({
				date: dateStr,
				timezone: userTimezone,
			}),
		);
	}, [dispatch, selectedDate, userTimezone]);

	const fetchFailedData = useCallback(() => {
		dispatch(
			getAutoDraftFailedAsAdmin({
				page: failedPage + 1,
				limit: failedRowsPerPage,
				timezone: userTimezone,
			}),
		);
	}, [dispatch, failedPage, failedRowsPerPage, userTimezone]);

	const fetchRetriesData = useCallback(() => {
		dispatch(
			getAutoDraftRetriesAsAdmin({
				page: retriesPage + 1,
				limit: retriesRowsPerPage,
			}),
		);
	}, [dispatch, retriesPage, retriesRowsPerPage]);

	useEffect(() => {
		if (activeTab === "results") {
			fetchResultsData();
		} else if (activeTab === "failed") {
			fetchFailedData();
		} else if (activeTab === "retries") {
			fetchRetriesData();
		}
	}, [activeTab, fetchResultsData, fetchFailedData, fetchRetriesData]);

	const handleTabChange = (
		_event: React.SyntheticEvent,
		newValue: TabValue,
	) => {
		setActiveTab(newValue);
		setSearchQuery("");
	};

	const handleDateChange = (newDate: Dayjs | null) => {
		if (newDate) {
			setSelectedDate(newDate);
			setResultsPage(0);
		}
	};

	const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(event.target.value);
		setResultsPage(0);
		setFailedPage(0);
		setRetriesPage(0);
	};

	const handleRefresh = () => {
		if (activeTab === "results") {
			fetchResultsData();
		} else if (activeTab === "failed") {
			fetchFailedData();
		} else if (activeTab === "retries") {
			fetchRetriesData();
		}
	};

	const handleDateSearch = (event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") {
			const dateInput = (event.target as HTMLInputElement).value;
			const parsedDate = dayjs(dateInput, "YYYY-MM-DD", true);
			if (parsedDate.isValid()) {
				setSelectedDate(parsedDate);
				setSearchQuery("");
			}
		}
	};

	// Filter attempts based on search query
	const filterAttempts = <T extends AutoDraftAttemptBase>(
		attempts: T[],
	): T[] => {
		if (!searchQuery) return attempts;
		const query = searchQuery.toLowerCase();
		return attempts.filter((attempt) => {
			const memberName = attempt.member?.name?.toLowerCase() || "";
			const memberEmail = attempt.member?.email?.toLowerCase() || "";
			// Use getCircleName helper to get circle name from adminCircles
			const circleName = getCircleName(
				attempt.circle?.circleId,
				attempt.circle?.name,
			).toLowerCase();
			return (
				memberName.includes(query) ||
				memberEmail.includes(query) ||
				circleName.includes(query)
			);
		});
	};

	// Status chip helper
	const getStatusChip = (status: string) => {
		switch (status) {
			case "success":
				return (
					<Chip
						label="Success"
						color="success"
						size="small"
						icon={<CheckCircleIcon />}
					/>
				);
			case "failed":
				return (
					<Chip
						label="Failed"
						color="error"
						size="small"
						icon={<ErrorIcon />}
					/>
				);
			case "retrying":
				return (
					<Chip
						label="Retrying"
						color="warning"
						size="small"
						icon={<RefreshIcon />}
					/>
				);
			case "pending":
				return (
					<Chip
						label="Pending"
						color="info"
						size="small"
						icon={<PendingIcon />}
					/>
				);
			default:
				return <Chip label={status} size="small" />;
		}
	};

	// Render Results Tab Content
	const renderResultsTab = () => {
		const summary = autoDraftResults?.summary;
		const attempts = autoDraftResults?.attempts;

		// Combine all attempts for display
		const allAttempts = [
			...(attempts?.successful || []),
			...(attempts?.failed || []),
			...(attempts?.retrying || []),
			...(attempts?.pending || []),
		];

		const filteredAttempts = filterAttempts(allAttempts);
		const paginatedAttempts = filteredAttempts.slice(
			resultsPage * resultsRowsPerPage,
			resultsPage * resultsRowsPerPage + resultsRowsPerPage,
		);

		return (
			<Stack spacing={3}>
				{/* Summary Cards */}
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "primary.lighter",
									}}>
									<TrendingUpIcon color="primary" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold">
										{summary?.total || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Total Attempts
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<CheckCircleIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{summary?.successful || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Successful
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "error.lighter",
									}}>
									<ErrorIcon color="error" />
								</Box>
								<Box>
									<Typography variant="h5" fontWeight="bold" color="error.main">
										{summary?.failed || 0}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Failed
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
					<Grid item xs={12} sm={6} md={3}>
						<Card sx={{ p: 2, height: "100%" }}>
							<Stack direction="row" spacing={2} alignItems="center">
								<Box
									sx={{
										p: 1.5,
										borderRadius: 2,
										backgroundColor: "success.lighter",
									}}>
									<AttachMoneyIcon color="success" />
								</Box>
								<Box>
									<Typography
										variant="h5"
										fontWeight="bold"
										color="success.main">
										{formatCurrency((summary?.totalAmountSuccessful || 0) / 100, "usd")}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										Amount Collected
									</Typography>
								</Box>
							</Stack>
						</Card>
					</Grid>
				</Grid>

				{/* Results Table */}
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "800px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Attempted At</TableCell>
									<TableCell>Retry Count</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{paginatedAttempts.length === 0 ?
									<TableRow>
										<TableCell colSpan={7} align="center">
											<Typography
												variant="body2"
												color="text.secondary"
												sx={{ py: 4 }}>
												No auto-draft attempts found for this date
											</Typography>
										</TableCell>
									</TableRow>
									: paginatedAttempts.map((attempt, index) => (
										<TableRow hover key={attempt.attemptId || index}>
											<TableCell>
												<Typography variant="subtitle2">
													{attempt.member?.name || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{attempt.member?.email || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{getCircleName(
														attempt.circle?.circleId,
														attempt.circle?.name,
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{attempt.amount?.formatted || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>{getStatusChip(attempt.status)}</TableCell>
											<TableCell>
												<Typography variant="body2">
													{dayjs(attempt.attemptedAt).format(
														"MMM D, YYYY h:mm A",
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={`${attempt.retryCount}/${attempt.maxRetries}`}
													size="small"
													variant="outlined"
												/>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={filteredAttempts.length}
						onPageChange={(_, newPage) => setResultsPage(newPage)}
						onRowsPerPageChange={(e) => {
							setResultsRowsPerPage(parseInt(e.target.value, 10));
							setResultsPage(0);
						}}
						page={resultsPage}
						rowsPerPage={resultsRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render Failed Tab Content
	const renderFailedTab = () => {
		const failedAttempts = autoDraftFailed?.attempts || [];
		const pagination = autoDraftFailed?.pagination;
		const failureAnalysis = autoDraftFailed?.failureAnalysis;

		const filteredAttempts = filterAttempts(failedAttempts);

		return (
			<Stack spacing={3}>
				{/* Failure Analysis Card */}
				{failureAnalysis && (
					<Card>
						<Box
							sx={{
								p: 2,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								cursor: "pointer",
							}}
							onClick={() =>
								setExpandedFailureAnalysis(!expandedFailureAnalysis)
							}>
							<Stack direction="row" spacing={1} alignItems="center">
								<WarningIcon color="warning" />
								<Typography variant="h6">Failure Analysis</Typography>
							</Stack>
							<IconButton size="small">
								{expandedFailureAnalysis ?
									<ExpandLessIcon />
									: <ExpandMoreIcon />}
							</IconButton>
						</Box>
						<Collapse in={expandedFailureAnalysis}>
							<Divider />
							<Box sx={{ p: 2 }}>
								<Grid container spacing={3}>
									<Grid item xs={12} md={6}>
										<Typography variant="subtitle2" gutterBottom>
											By Reason
										</Typography>
										<Stack spacing={1}>
											{Object.entries(failureAnalysis.byReason || {}).map(
												([reason, count]) => (
													<Stack
														key={reason}
														direction="row"
														justifyContent="space-between"
														alignItems="center">
														<Typography variant="body2" color="text.secondary">
															{reason}
														</Typography>
														<Chip label={count} size="small" color="error" />
													</Stack>
												),
											)}
										</Stack>
									</Grid>
									<Grid item xs={12} md={6}>
										<Typography variant="subtitle2" gutterBottom>
											By Error Code
										</Typography>
										<Stack spacing={1}>
											{Object.entries(failureAnalysis.byCode || {}).map(
												([code, count]) => (
													<Stack
														key={code}
														direction="row"
														justifyContent="space-between"
														alignItems="center">
														<Typography
															variant="body2"
															color="text.secondary"
															sx={{ fontFamily: "monospace" }}>
															{code}
														</Typography>
														<Chip
															label={count}
															size="small"
															variant="outlined"
														/>
													</Stack>
												),
											)}
										</Stack>
									</Grid>
								</Grid>
							</Box>
						</Collapse>
					</Card>
				)}

				{/* Failed Attempts Table */}
				<Card>
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "900px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Failure Reason</TableCell>
									<TableCell>Error Code</TableCell>
									<TableCell>Attempted At</TableCell>
									<TableCell>Retries</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredAttempts.length === 0 ?
									<TableRow>
										<TableCell colSpan={8} align="center">
											<Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
												<CheckCircleIcon
													sx={{ fontSize: 48, color: "success.main" }}
												/>
												<Typography variant="body2" color="text.secondary">
													No failed auto-draft attempts found
												</Typography>
											</Stack>
										</TableCell>
									</TableRow>
									: filteredAttempts.map((attempt, index) => (
										<TableRow hover key={attempt.attemptId || index}>
											<TableCell>
												<Typography variant="subtitle2">
													{attempt.member?.name || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{attempt.member?.email || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{getCircleName(
														attempt.circle?.circleId,
														attempt.circle?.name,
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{attempt.amount?.formatted || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Tooltip title={attempt.failureReason || "Unknown"}>
													<Typography
														variant="body2"
														color="error.main"
														sx={{
															maxWidth: 200,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}>
														{attempt.failureReason || "Unknown"}
													</Typography>
												</Tooltip>
											</TableCell>
											<TableCell>
												<Chip
													label={attempt.failureCode || "N/A"}
													size="small"
													color="error"
													variant="outlined"
													sx={{ fontFamily: "monospace", fontSize: "0.75rem" }}
												/>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{dayjs(attempt.attemptedAt).format(
														"MMM D, YYYY h:mm A",
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={`${attempt.retryCount}/${attempt.maxRetries}`}
													size="small"
													variant="outlined"
													color={
														attempt.retryCount >= attempt.maxRetries ?
															"error"
															: "default"
													}
												/>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={pagination?.total || filteredAttempts.length}
						onPageChange={(_, newPage) => setFailedPage(newPage)}
						onRowsPerPageChange={(e) => {
							setFailedRowsPerPage(parseInt(e.target.value, 10));
							setFailedPage(0);
						}}
						page={failedPage}
						rowsPerPage={failedRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	// Render Retries Tab Content
	const renderRetriesTab = () => {
		const retryAttempts = autoDraftRetries?.attempts || [];
		const upcomingRetries = autoDraftRetries?.upcomingRetries || [];
		const pagination = autoDraftRetries?.pagination;

		const filteredAttempts = filterAttempts(retryAttempts);

		return (
			<Stack spacing={3}>
				{/* Upcoming Retries Alert */}
				{upcomingRetries.length > 0 && (
					<Alert
						severity="info"
						icon={<ScheduleIcon />}
						sx={{ alignItems: "center" }}>
						<Typography variant="body2">
							<strong>{upcomingRetries.length}</strong> retry attempt
							{upcomingRetries.length > 1 ? "s" : ""} scheduled within the next
							24 hours
						</Typography>
					</Alert>
				)}

				{/* Upcoming Retries Card */}
				{upcomingRetries.length > 0 && (
					<Card>
						<Box sx={{ p: 2 }}>
							<Stack direction="row" spacing={1} alignItems="center">
								<ScheduleIcon color="info" />
								<Typography variant="h6">
									Upcoming Retries (Next 24h)
								</Typography>
							</Stack>
						</Box>
						<Divider />
						<Box sx={{ overflowX: "auto" }}>
							<Table>
								<TableHead>
									<TableRow>
										<TableCell>Member</TableCell>
										<TableCell>Circle</TableCell>
										<TableCell>Amount</TableCell>
										<TableCell>Scheduled Retry</TableCell>
										<TableCell>Retry Count</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>
									{upcomingRetries.slice(0, 5).map((attempt, index) => (
										<TableRow hover key={attempt.attemptId || index}>
											<TableCell>
												<Typography variant="subtitle2">
													{attempt.member?.name || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{getCircleName(
														attempt.circle?.circleId,
														attempt.circle?.name,
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{attempt.amount?.formatted || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Chip
													label={dayjs(attempt.nextRetryAt).format(
														"MMM D, h:mm A",
													)}
													size="small"
													color="info"
													icon={<ScheduleIcon />}
												/>
											</TableCell>
											<TableCell>
												<Chip
													label={`${attempt.retryCount}/${attempt.maxRetries}`}
													size="small"
													variant="outlined"
												/>
											</TableCell>
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					</Card>
				)}

				{/* All Retry/Pending Attempts Table */}
				<Card>
					<Box sx={{ p: 2 }}>
						<Stack direction="row" spacing={1} alignItems="center">
							<RefreshIcon color="warning" />
							<Typography variant="h6">All Pending Retries</Typography>
						</Stack>
					</Box>
					<Divider />
					<Box sx={{ overflowX: "auto" }}>
						<Table sx={{ minWidth: "900px" }}>
							<TableHead>
								<TableRow>
									<TableCell>Member</TableCell>
									<TableCell>Email</TableCell>
									<TableCell>Circle</TableCell>
									<TableCell>Amount</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Last Failure</TableCell>
									<TableCell>Next Retry</TableCell>
									<TableCell>Retries</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredAttempts.length === 0 ?
									<TableRow>
										<TableCell colSpan={8} align="center">
											<Stack spacing={1} alignItems="center" sx={{ py: 4 }}>
												<CheckCircleIcon
													sx={{ fontSize: 48, color: "success.main" }}
												/>
												<Typography variant="body2" color="text.secondary">
													No pending retry attempts
												</Typography>
											</Stack>
										</TableCell>
									</TableRow>
									: filteredAttempts.map((attempt, index) => (
										<TableRow hover key={attempt.attemptId || index}>
											<TableCell>
												<Typography variant="subtitle2">
													{attempt.member?.name || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" color="text.secondary">
													{attempt.member?.email || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2">
													{getCircleName(
														attempt.circle?.circleId,
														attempt.circle?.name,
													)}
												</Typography>
											</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="medium">
													{attempt.amount?.formatted || "N/A"}
												</Typography>
											</TableCell>
											<TableCell>{getStatusChip(attempt.status)}</TableCell>
											<TableCell>
												<Tooltip title={attempt.failureReason || "N/A"}>
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{
															maxWidth: 150,
															overflow: "hidden",
															textOverflow: "ellipsis",
															whiteSpace: "nowrap",
														}}>
														{attempt.failureReason || "N/A"}
													</Typography>
												</Tooltip>
											</TableCell>
											<TableCell>
												{attempt.nextRetryAt ?
													<Chip
														label={dayjs(attempt.nextRetryAt).format(
															"MMM D, h:mm A",
														)}
														size="small"
														color="info"
														variant="outlined"
													/>
													: <Typography variant="body2" color="text.secondary">
														N/A
													</Typography>
												}
											</TableCell>
											<TableCell>
												<Chip
													label={`${attempt.retryCount}/${attempt.maxRetries}`}
													size="small"
													variant="outlined"
													color={
														attempt.retryCount >= attempt.maxRetries ?
															"error"
															: "warning"
													}
												/>
											</TableCell>
										</TableRow>
									))
								}
							</TableBody>
						</Table>
					</Box>
					<Divider />
					<TablePagination
						component="div"
						count={pagination?.total || filteredAttempts.length}
						onPageChange={(_, newPage) => setRetriesPage(newPage)}
						onRowsPerPageChange={(e) => {
							setRetriesRowsPerPage(parseInt(e.target.value, 10));
							setRetriesPage(0);
						}}
						page={retriesPage}
						rowsPerPage={retriesRowsPerPage}
						rowsPerPageOptions={[5, 10, 25, 50]}
					/>
				</Card>
			</Stack>
		);
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<React.Fragment>
				<UserProfileHeader onOpenNav={() => setOpenNav(true)} />
				<Box
					sx={{
						minHeight: 1,
						display: "flex",
						flexDirection: { xs: "column", lg: "row" },
					}}>
					<DrawerNavbar
						openNav={openNav}
						onCloseNav={() => setOpenNav(false)}
					/>

					<Main>
						{(
							isLoading &&
							!autoDraftResults &&
							!autoDraftFailed &&
							!autoDraftRetries
						) ?
							<Box
								sx={{
									display: "flex",
									justifyContent: "center",
									alignItems: "center",
									minHeight: "60vh",
								}}>
								<LoadingSpinner loading={isLoading} size={45} />
							</Box>
							: <Stack spacing={3}>
								{/* Header */}
								<Stack direction="row" spacing={3}>
									<Stack spacing={1} sx={{ flex: "1 1 auto" }}>
										<Typography variant="h4">Auto-Draft Reports</Typography>
										<Typography color="text.secondary" variant="body2">
											View and analyze auto-draft payment attempts
										</Typography>
									</Stack>
									<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
										<Tooltip title="Refresh">
											<IconButton onClick={handleRefresh} disabled={isLoading}>
												{isLoading ?
													<CircularProgress size={24} />
													: <ArrowsClockwise />}
											</IconButton>
										</Tooltip>
									</Box>
								</Stack>

								{/* Error Alert */}
								{error && (
									<Alert severity="error" onClose={() => { }}>
										{error}
									</Alert>
								)}

								{/* Date Picker and Search Section */}
								<Card sx={{ p: 2 }}>
									<Grid container spacing={2} alignItems="center">
										<Grid item xs={12} md={4}>
											<Stack direction="row" spacing={1} alignItems="center">
												<CalendarMonthIcon color="primary" />
												<TextField
													label="Selected Date"
													value={selectedDate.format("YYYY-MM-DD")}
													size="small"
													InputProps={{
														readOnly: true,
													}}
													onClick={() => setShowCalendar(!showCalendar)}
													sx={{ cursor: "pointer" }}
												/>
												<IconButton
													onClick={() => setShowCalendar(!showCalendar)}
													color={showCalendar ? "primary" : "default"}>
													<CalendarMonthIcon />
												</IconButton>
											</Stack>
										</Grid>
										<Grid item xs={12} md={4}>
											<OutlinedInput
												value={searchQuery}
												onChange={handleSearchChange}
												onKeyDown={handleDateSearch}
												fullWidth
												size="small"
												placeholder="Search by name, email, or circle... (or enter date YYYY-MM-DD)"
												startAdornment={
													<InputAdornment position="start">
														<MagnifyingGlassIcon fontSize="var(--icon-fontSize-md)" />
													</InputAdornment>
												}
											/>
										</Grid>
										<Grid item xs={12} md={4}>
											<Stack direction="row" spacing={1} alignItems="center">
												<InfoOutlinedIcon color="action" fontSize="small" />
												<Typography variant="body2" color="text.secondary">
													Timezone: {autoDraftResults?.timezone || userTimezone}
												</Typography>
											</Stack>
										</Grid>
									</Grid>

									{/* Calendar Dropdown */}
									<Collapse in={showCalendar}>
										<Box
											sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
											<Paper elevation={3} sx={{ borderRadius: 2 }}>
												<DateCalendar
													value={selectedDate}
													onChange={handleDateChange}
													slots={{
														day: CustomPickersDay,
													}}
													maxDate={dayjs()}
												/>
											</Paper>
										</Box>
									</Collapse>
								</Card>

								{/* Tabs */}
								<Card>
									<Tabs
										value={activeTab}
										onChange={handleTabChange}
										indicatorColor="primary"
										textColor="primary"
										variant="fullWidth">
										<Tab
											label={
												<Stack direction="row" spacing={1} alignItems="center">
													<TrendingUpIcon />
													<span>Results</span>
													{autoDraftResults?.summary?.total !== undefined && (
														<Chip
															label={autoDraftResults.summary.total}
															size="small"
															color="primary"
														/>
													)}
												</Stack>
											}
											value="results"
										/>
										<Tab
											label={
												<Stack direction="row" spacing={1} alignItems="center">
													<ErrorIcon />
													<span>Failed</span>
													{autoDraftFailed?.pagination?.total !== undefined && (
														<Chip
															label={autoDraftFailed.pagination.total}
															size="small"
															color="error"
														/>
													)}
												</Stack>
											}
											value="failed"
										/>
										<Tab
											label={
												<Stack direction="row" spacing={1} alignItems="center">
													<RefreshIcon />
													<span>Retries</span>
													{autoDraftRetries?.pagination?.total !==
														undefined && (
															<Chip
																label={autoDraftRetries.pagination.total}
																size="small"
																color="warning"
															/>
														)}
												</Stack>
											}
											value="retries"
										/>
									</Tabs>
								</Card>

								{/* Tab Content */}
								{isLoading ?
									<Box
										sx={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
											minHeight: 300,
										}}>
										<CircularProgress />
									</Box>
									: <>
										{activeTab === "results" && renderResultsTab()}
										{activeTab === "failed" && renderFailedTab()}
										{activeTab === "retries" && renderRetriesTab()}
									</>
								}
							</Stack>
						}
					</Main>
				</Box>
			</React.Fragment>
		</LocalizationProvider>
	);
};

export default AutoDraftReports;
